import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import api from '../../lib/api';
import PesoLayout from './PesoLayout';
import { OfficeCtx, useOffice } from './officeCtxRef';
import { PANGLAO_BARANGAYS, PANGLAO_JOB_CATEGORIES, PANGLAO_TO_BT_CATEGORY } from './pesoFields';

/**
 * Loads an LGU PESO office config (GET /api/peso/office/:slug) from the `:lgu` route
 * param and exposes it + derived helpers to the portal pages. This is what makes the
 * portal multi-tenant: branding, barangays, categories, and the per-office
 * registration_source / referral code / peso_office tag all come from here, not from
 * hardcoded Panglao constants (which remain only as a safety fallback).
 */
export { useOffice };

export function PesoOfficeProvider({ children }) {
  const { lgu } = useParams();
  const slug = (lgu || '').toLowerCase();
  const [state, setState] = useState({ loading: true, error: null, office: null });

  useEffect(() => {
    let active = true;
    setState({ loading: true, error: null, office: null });
    api.get(`/api/peso/office/${slug}`)
      .then((res) => { if (active) setState({ loading: false, error: null, office: res.data }); })
      .catch((err) => {
        if (active) setState({
          loading: false,
          error: err?.response?.status === 404 ? 'not_found' : 'error',
          office: null,
        });
      });
    return () => { active = false; };
  }, [slug]);

  const value = useMemo(() => {
    const office = state.office;
    if (!office) return null;
    const cats = Array.isArray(office.categories) && office.categories.length ? office.categories : null;
    return {
      office,
      slug: office.slug,
      registrationSource: `peso_${office.slug}`,
      referralCode: `PESO-${String(office.slug).toUpperCase()}`,
      pesoOffice: office.slug,
      // Best-effort municipality name for address defaults (e.g. "PESO Panglao" -> "Panglao")
      municipality: String(office.name || '').replace(/^PESO\s+/i, '').trim() || office.slug,
      barangays: Array.isArray(office.barangays) && office.barangays.length ? office.barangays : PANGLAO_BARANGAYS,
      categoryLabels: cats ? cats.map((c) => c.label) : PANGLAO_JOB_CATEGORIES,
      categoryMap: cats
        ? Object.fromEntries(cats.map((c) => [c.label, c.bt_category || 'Other']))
        : PANGLAO_TO_BT_CATEGORY,
    };
  }, [state.office]);

  if (state.loading) {
    return <PesoLayout><div className="py-16 text-center text-slate-500">Loading…</div></PesoLayout>;
  }
  if (state.error === 'not_found') {
    return <Navigate to="/" replace />;
  }
  if (state.error) {
    return <PesoLayout><div className="py-16 text-center text-red-600">Could not load this PESO office. Please try again later.</div></PesoLayout>;
  }

  return <OfficeCtx.Provider value={value}>{children}</OfficeCtx.Provider>;
}
